import pickBy from 'lodash/pickBy';
import { isWorthy } from 'utils/isWorthy.ts';
import { type LocalityData, normalizeLocality, type ResponseLocalityData } from 'service/locality/model.ts';
import type { ModalWatchdogDataFilter } from 'components/modal/watchdog/WatchdogModal.tsx';
import { localityToFilterLocality } from 'signals/filters';

export type WatchdogNotificationInterval = 'daily' | 'never' | 'hourly';

export type RequestCreateWatchdogFilter = Omit<ResponseWatchdogDataFilter, 'locality'> & {
	locality?: Pick<ResponseLocalityData, 'entity_id' | 'entity_type'>;
};

export type RequestCreateWatchdog = RequestCreateWatchdogFilter & {
	notification_interval: WatchdogNotificationInterval;
};

export type ResponseCreateWatchdog = {
	id: number;
	status_code: number;
	status_message: 'Created';
	watchdog_hash: string;
};

export type ResponseWatchdogDataFilter = {
	category_id?: number;
	hide_price_by_agreement?: true | false;
	locality?: ResponseLocalityData;
	phrase?: string;
	price_for_free?: true | false;
	price_from?: number;
	price_to?: number;
	radius_precision?: number; // km
};

export type WatchdogDataFilter = {
	categoryId?: number;
	hidePriceByAgreement?: true | false;
	locality?: LocalityData;
	phrase?: string;
	priceForFree?: true | false;
	priceFrom?: number;
	priceTo?: number;
	radiusPrecision?: number; // km
};

export type ResponseWatchdogData = {
	create_date: string; // ISO date
	filter: ResponseWatchdogDataFilter;
	id: number;
	notification_enabled: boolean;
	notification_interval: WatchdogNotificationInterval;
	operating_lease: boolean;
	service_name: 'sbazar';
	timestamp: string; // ISO date
	watchdog_hash: string;
};

export type ResponseWatchdogCountData = ResponseWatchdogData & {
	count: number;
	new_count: number;
};

export type WatchdogData = {
	createDate: Date;
	filter: WatchdogDataFilter;
	id: number;
	notificationEnabled: boolean;
	notificationInterval: WatchdogNotificationInterval;
	operatingLease: boolean;
	serviceName: 'sbazar';
	timestamp: Date;
	watchdogHash: string;
};

export type WatchdogCountData = WatchdogData & {
	count: number;
	newCount: number;
};

export function normalizeWatchdogFilter(responseWatchdogFilter: ResponseWatchdogData['filter']): WatchdogDataFilter {
	return pickBy(
		{
			categoryId: responseWatchdogFilter.category_id ? Number(responseWatchdogFilter.category_id) : undefined,
			hidePriceByAgreement: responseWatchdogFilter.hide_price_by_agreement ?? false,
			locality: responseWatchdogFilter.locality ? normalizeLocality(responseWatchdogFilter.locality) : undefined,
			phrase: responseWatchdogFilter.phrase ? responseWatchdogFilter.phrase : undefined,
			priceForFree: responseWatchdogFilter.price_for_free ?? false,
			priceFrom:
				responseWatchdogFilter.price_from === undefined ? undefined : Number(responseWatchdogFilter.price_from),
			priceTo:
				responseWatchdogFilter.price_to === undefined ? undefined : Number(responseWatchdogFilter.price_to),
			radiusPrecision:
				responseWatchdogFilter.radius_precision === undefined
					? undefined
					: Number(responseWatchdogFilter.radius_precision),
		},
		isWorthy,
	);
}

export function modalizeWatchdogFilter(responseWatchdogFilter: WatchdogData['filter']): ModalWatchdogDataFilter {
	return pickBy(
		{
			category_id: responseWatchdogFilter.categoryId ? Number(responseWatchdogFilter.categoryId) : undefined,
			hide_price_by_agreement: responseWatchdogFilter.hidePriceByAgreement ?? false,
			locality: responseWatchdogFilter.locality
				? localityToFilterLocality(responseWatchdogFilter.locality)
				: undefined,
			phrase: responseWatchdogFilter.phrase ? responseWatchdogFilter.phrase : undefined,
			price_for_free: responseWatchdogFilter.priceForFree ?? false,
			price_from:
				responseWatchdogFilter.priceFrom === undefined ? undefined : Number(responseWatchdogFilter.priceFrom),
			price_to: responseWatchdogFilter.priceTo === undefined ? undefined : Number(responseWatchdogFilter.priceTo),
			radius_precision:
				responseWatchdogFilter.radiusPrecision === undefined
					? undefined
					: Number(responseWatchdogFilter.radiusPrecision),
		},
		isWorthy,
	);
}

export function normalizeWatchdog(watchdog: ResponseWatchdogData): WatchdogData {
	return {
		createDate: new Date(watchdog.create_date),
		filter: normalizeWatchdogFilter(watchdog.filter),
		id: watchdog.id,
		notificationEnabled: watchdog.notification_enabled,
		notificationInterval: watchdog.notification_interval,
		operatingLease: watchdog.operating_lease,
		serviceName: watchdog.service_name,
		timestamp: new Date(watchdog.timestamp),
		watchdogHash: watchdog.watchdog_hash,
	};
}

export function normalizeWatchdogCount(watchdog: ResponseWatchdogCountData): WatchdogCountData {
	return {
		createDate: new Date(watchdog.create_date),
		filter: normalizeWatchdogFilter(watchdog.filter),
		id: watchdog.id,
		notificationEnabled: watchdog.notification_enabled,
		notificationInterval: watchdog.notification_interval,
		operatingLease: watchdog.operating_lease,
		serviceName: watchdog.service_name,
		timestamp: new Date(watchdog.timestamp),
		watchdogHash: watchdog.watchdog_hash,
		count: watchdog.count,
		newCount: watchdog.new_count,
	};
}

export type ResponseWatchdogsSummary = {
	new_adverts_count: number;
	watchdogs_count: number;
};

export type WatchdogsSummary = {
	newOffersCount: number;
	watchdogsCount: number;
};

export function normalizeWatchdogsSummary(summary: ResponseWatchdogsSummary): WatchdogsSummary {
	return { newOffersCount: summary.new_adverts_count, watchdogsCount: summary.watchdogs_count };
}
